import { FolderNode } from "@/features/upload/components/UploadFolderPopup/types/folderUpload";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface FolderUploadFile {
  uniqueId: string; // Unique identifier to differentiate files with same name in different folders
  file: File;
  folderId: string;
  status: "pending" | "uploading" | "completed" | "failed";
  progress: number;
  error?: string;
  fileName: string; // Add fileName for tracking
  // Context per file to handle workspace switching
  isOrganization?: boolean;
  projectId?: string;
  companyId?: string;
}

export interface FolderUploadState {
  isUploading: boolean;
  isVisible: boolean;
  folderName: string;
  parentFolderId: string;
  rootFolderId: string;
  totalFiles: number;
  uploadedFiles: number;
  failedFiles: number;
  filesQueue: FolderUploadFile[];
  currentFileIndex: number;
  error?: string;
  trackedFileNames: string[]; // Track which files belong to this folder upload (changed to array for Redux serialization)
  // Queue management
  pendingUploads: FolderUploadFile[]; // Files waiting to be uploaded
  activeUploads: FolderUploadFile[]; // Files currently being uploaded
  maxConcurrentUploads: number; // Maximum number of concurrent uploads (default: 5)
  isStopped: boolean; // Flag to stop processing new files from queue
}

const initialState: FolderUploadState = {
  isUploading: false,
  isVisible: false,
  folderName: "",
  parentFolderId: "",
  rootFolderId: "",
  totalFiles: 0,
  uploadedFiles: 0,
  failedFiles: 0,
  filesQueue: [],
  currentFileIndex: -1,
  error: undefined,
  trackedFileNames: [],
  // Queue management
  pendingUploads: [],
  activeUploads: [],
  maxConcurrentUploads: 5,
  isStopped: false,
};

const folderUploadSlice = createSlice({
  name: "folderUpload",
  initialState,
  reducers: {
    // Start folder upload process
    startFolderUpload: (
      state,
      action: PayloadAction<{
  source: 'input' | 'drag-drop';
  rootFolderName: string;
  folderStructure: FolderNode[];
  projectId: string;
  parentFolderId: string;
  isOrganization?: boolean;
      }>
    ) => {
      state.isUploading = true;
      state.isVisible = true;
      state.folderName = action.payload.rootFolderName;
      state.parentFolderId = action.payload.parentFolderId;
      state.rootFolderId = "";
      state.totalFiles = 0;
      state.uploadedFiles = 0;
      state.failedFiles = 0;
      state.currentFileIndex = 0;
      state.error = undefined;
      state.trackedFileNames = [];
      state.pendingUploads = [];
      state.activeUploads = [];
    },

    // Update file status in queue
    updateFileStatus: (
      state,
      action: PayloadAction<{
        index: number;
        status: "pending" | "uploading" | "completed" | "failed";
        progress?: number;
        error?: string;
        folderId?: string;
      }>
    ) => {
      const { index, status, progress, error, folderId } = action.payload;
      if (state.filesQueue[index]) {
        state.filesQueue[index].status = status;
        if (progress !== undefined) {
          state.filesQueue[index].progress = progress;
        }
        if (error) {
          state.filesQueue[index].error = error;
        }
        if (folderId) {
          state.filesQueue[index].folderId = folderId;
        }
      }
    },

    // Increment uploaded files counter
    incrementUploadedFiles: (state) => {
      state.uploadedFiles += 1;
    },

    // Increment failed files counter
    incrementFailedFiles: (state) => {
      state.failedFiles += 1;
    },

    // Set current file index
    setCurrentFileIndex: (state, action: PayloadAction<number>) => {
      state.currentFileIndex = action.payload;
    },

    // Folder structure creation completed
    folderStructureCreated: (
      state,
      action: PayloadAction<{
        filesWithFolders: Array<{ file: File; folderId: string }>;
      }>
    ) => {
      // Update files queue with folder IDs
      // Match by uniqueId from file object to handle duplicate filenames in different folders
      action.payload.filesWithFolders.forEach(({ file, folderId }) => {
        const uniqueId = (file as any).__uniqueId;
        const index = state.filesQueue.findIndex(
          (f) => f.uniqueId === uniqueId
        );
        if (index !== -1) {
          state.filesQueue[index].folderId = folderId;
        } else {
          // If not found in queue, add it
          state.filesQueue.push({
            uniqueId,
            file,
            folderId,
            status: "pending",
            progress: 0,
            fileName: file.name,
          });
        }
      });
    },

    // Complete folder upload
    completeFolderUpload: (state) => {
      state.isUploading = false;
      state.currentFileIndex = -1;
    },

    setRootFolderId: (state, action: PayloadAction<string>) => {
      state.rootFolderId = action.payload;
    },

    // Close popup
    closePopup: (state) => {
      state.isVisible = false;
    },

    // Reset state
    resetFolderUpload: () => initialState,

    // Set error
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isUploading = false;
    },

    // Track individual file completion (called from epic)
    trackFileCompletion: (state, action: PayloadAction<{ fileName: string }>) => {
      if (state.trackedFileNames.includes(action.payload.fileName)) {
        const fileIndex = state.filesQueue.findIndex(
          (f) => f.fileName === action.payload.fileName
        );
        if (fileIndex !== -1 && state.filesQueue[fileIndex].status !== "completed") {
          state.filesQueue[fileIndex].status = "completed";
          state.uploadedFiles += 1;
          
        }
      }
    },

    // Track individual file failure (called from epic)
    trackFileFailure: (state, action: PayloadAction<{ fileName: string }>) => {
      if (state.trackedFileNames.includes(action.payload.fileName)) {
        const fileIndex = state.filesQueue.findIndex(
          (f) => f.fileName === action.payload.fileName
        );
        if (fileIndex !== -1 && state.filesQueue[fileIndex].status !== "failed") {
          state.filesQueue[fileIndex].status = "failed";
          state.failedFiles += 1;
          
        }
      }
    },

    // Queue management actions
    addFilesToQueue: (state, action: PayloadAction<FolderUploadFile[]>) => {
      state.pendingUploads.push(...action.payload);
      state.totalFiles = (state.totalFiles ?? 0) + action.payload.length;
      state.filesQueue = [...state.pendingUploads, ...state.activeUploads];
      state.trackedFileNames = action.payload.map(f => f.fileName);
    },

    // Move file from pending to active
    startFileUpload: (state, action: PayloadAction<{ fileName: string; uniqueId?: string }>) => {
      const fileIndex = state.pendingUploads.findIndex(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.fileName === action.payload.fileName
      );
      if (fileIndex !== -1) {
        const file = state.pendingUploads.splice(fileIndex, 1)[0];
        file.status = "uploading";
        state.activeUploads.push(file);
        
        // Update filesQueue
        state.filesQueue = [...state.pendingUploads, ...state.activeUploads];
      } else {
        console.warn(`⚠️ [startFileUpload] File not found in pending: ${action.payload.uniqueId || action.payload.fileName}`);
      }
    },

    // Remove file from active uploads when done
    completeFileUpload: (state, action: PayloadAction<{ fileName?: string; uniqueId?: string; success: boolean }>) => {
      const fileIndex = state.activeUploads.findIndex(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.fileName === action.payload.fileName
      );
      if (fileIndex !== -1) {
        const file = state.activeUploads.splice(fileIndex, 1)[0];
        file.status = action.payload.success ? "completed" : "failed";
        
        if (action.payload.success) {
          state.uploadedFiles += 1;
        } else {
          state.failedFiles += 1;
        }
        
        // Update filesQueue
        state.filesQueue = [...state.pendingUploads, ...state.activeUploads];
      } else {
        console.warn(`⚠️ [completeFileUpload] File not found in active: ${action.payload.uniqueId || action.payload.fileName}`);
      }
    },

    // Update progress for a file in active uploads
    updateActiveFileProgress: (state, action: PayloadAction<{ fileName?: string; uniqueId?: string; progress: number }>) => {
      const fileIndex = state.activeUploads.findIndex(
        (f) => action.payload.uniqueId ? f.uniqueId === action.payload.uniqueId : f.fileName === action.payload.fileName
      );
      if (fileIndex !== -1) {
        state.activeUploads[fileIndex].progress = action.payload.progress;
      }
    },
    resetFolderUploadState: () => initialState,
    setTotalFiles: (state, action: PayloadAction<number>) => {
      state.totalFiles += action.payload;
    },
    stopUpload: (state) => {
      state.isStopped = true;
    },
    resetPendingUploads: (state) => {
      state.pendingUploads = [];
      state.filesQueue = [];
    }
  },
});

export const folderUploadActions = folderUploadSlice.actions;
export default folderUploadSlice.reducer;
